import React, { useState, useEffect, useCallback, useRef } from "react";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { formatTimeAgo, formattedDateTimeForLocale, formattedTimeForLocale } from "helpers/Datetime.helper";
import { saveDiaryViewToLocalStorage } from "helpers/LocalStorage.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import { usePopup } from "contexts/PopupContext";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import { IDiary } from "api/interfaces/profile.interface";
import { getStudentDiaries, IStudentDiariesResult } from "api/agency/v2/home.api";
import { toggleDiaryLike, toggleDiaryBookmark, translateDiary, ITranslateResponse } from "api/agency/v2/diary.api";
import FullPageStudentInfoPopupContainer from "../popup/FullPageStudentInfoPopup.container";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import LikePopupForDiary from "pages/agency/v2/popup/LikePopupForDiary";

import StudentProfilePopup from "../popup/StudentProfilePopup";
import StudentInfoPopup from "pages/agency/v2/popup/StudentInfoPopup.container";
import StudentDiaryDetailPopup from "../popup/StudentDiaryDetailPopup";
import { getStudentNickname } from "helpers/String.helper";





// 날짜 헤더 컴포넌트
const DateHeader: React.FC<{ date: string }> = ({ date }) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        // 날짜 비교를 위해 시간 부분 제거
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        
        if (dateOnly.getTime() === todayOnly.getTime()) {
            return "오늘";
        } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
            return "어제";
        } else {
            const formattedDate = date.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            const weekday = date.toLocaleDateString('ko-KR', {
                weekday: 'short'
            });
            return `${formattedDate} (${weekday})`;
        }
    };

    return (
        <div className="flex items-center py-2">
            <div className="flex-1 border-t border-gray-100"></div>
            <div className="px-3 py-1">
                <span className="text-xs text-gray-400">
                    {formatDate(date)}
                </span>
            </div>
            <div className="flex-1 border-t border-gray-100"></div>
        </div>
    );
};

// 다이어리 아이템 컴포넌트 (2초 노출 감지 포함)
const DiaryItem: React.FC<{
    diary: IDiary;
    isLiking: boolean;
    isBookmarking: boolean;
    isTranslating: boolean;
    translatedData?: ITranslateResponse;
    onLikeToggle: (diary: IDiary, e: React.MouseEvent) => void;
    onBookmarkToggle: (diary: IDiary, e: React.MouseEvent) => void;
    onTranslate: (diary: IDiary, e: React.MouseEvent) => void;
    onCommentClick: (diaryId: number, e: React.MouseEvent) => void;
    onStudentInfoPopup: (userStudentId: number) => void;
    onImageClick: (images: any[], initialIndex: number) => void;
    onShowLikePopup: (diaryId: number) => void;
}> = ({ diary, isLiking, isBookmarking, isTranslating, translatedData, onLikeToggle, onBookmarkToggle, onTranslate, onCommentClick, onStudentInfoPopup, onImageClick, onShowLikePopup }) => {
    const [showExtraDiaries, setShowExtraDiaries] = useState(false);
    const { showPopup } = usePopup();
    const itemRef = useRef<HTMLDivElement>(null);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasBeenVisible) {
                        // 화면에 노출되기 시작 - 타이머 시작
                        timer = setTimeout(() => {
                            console.log("📊 다이어리 2초 노출 감지:", {
                                diaryId: diary.id,
                                title: diary.title,
                                studentName: diary.user?.userStudent?.nickname || diary.user?.username,
                                timestamp: new Date().toISOString()
                            });

                            // localStorage에 노출된 다이어리 저장
                            saveDiaryViewToLocalStorage(diary.id);

                            setHasBeenVisible(true);
                        }, 2000);
                    } else if (!entry.isIntersecting && timer) {
                        // 화면에서 벗어남 - 타이머 취소
                        clearTimeout(timer);
                        timer = null;
                    }
                });
            },
            { threshold: 0.5 } // 50% 이상 노출되었을 때 감지
        );

        if (itemRef.current) {
            observer.observe(itemRef.current);
        }

        return () => {
            if (itemRef.current) {
                observer.unobserve(itemRef.current);
            }
            // 타이머 정리
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [diary.id, diary.title, diary.user?.userStudent?.nickname, diary.user?.username, hasBeenVisible]);

    // 추가 다이어리 상세 팝업 호출 함수
    const handleExtraDiaryClick = (extraDiaryId: number) => {
        const popupContent = (
            <StudentDiaryDetailPopup
                diaryId={extraDiaryId.toString()}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    return (
        <div
            ref={itemRef}
            className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
            onClick={() => {
                // TODO: 다이어리 상세 팝업 또는 페이지 이동 구현
                console.log("다이어리 클릭:", diary.id);
            }}
        >
            {/* 다이어리 헤더 */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                        <img
                            src={getImageUrl(diary.user?.userStudent?.profileImage, 80, 80)}
                            alt="Student Profile"
                            className="w-full h-full object-cover"
                            onClick={() => onStudentInfoPopup(diary.user?.userStudent?.id)}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "/imgs/dummy/basic-img.png";
                            }}
                        />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800 text-sm"
                            onClick={() => onStudentInfoPopup(diary.user?.userStudent?.id)}
                        >
                            {getStudentNickname(diary.user?.userStudent)}
                            {/* {diary.user?.userStudent?.nickname || diary.user?.username || "알 수 없음"} */}
                        </h3>
                        <p className="text-xs text-gray-400">
                            {formattedDateTimeForLocale(diary.createdAt, 'ko-KR')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    {/* 포트폴리오 배지 */}
                    {diary.isPortfolio && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            포트폴리오
                        </div>
                    )}

                    {/* 번역하기 버튼 */}
                    <div
                        className={`flex flex-col items-center space-y-1 transition-all duration-200 ${isTranslating
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer hover:opacity-80'
                            }`}
                        onClick={(e) => !isTranslating && onTranslate(diary, e)}
                    >
                        {isTranslating ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 번역 아이콘
                            <img 
                                src={translatedData ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                alt="translate" 
                                className="w-4 h-4" 
                            />
                        )}
                        <span className={`text-xs font-medium ${translatedData ? 'text-[#CF3688]' : 'text-gray-400'}`}>
                            번역
                        </span>
                    </div>

                    {/* 북마크 아이콘 */}
                    <div
                        className={`flex flex-col items-center space-y-1 transition-all duration-200 ${isBookmarking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer hover:opacity-80'
                            }`}
                        onClick={(e) => !isBookmarking && onBookmarkToggle(diary, e)}
                    >
                        {isBookmarking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 북마크 아이콘
                            diary.isBookmarked ? (
                                <img src="/imgs/icons/icon-bookmark-active.svg" alt="bookmark" className="w-4 h-4" />
                            ) : (
                                <img src="/imgs/icons/icon-bookmark.svg" alt="bookmark" className="w-4 h-4" />
                            )
                        )}
                        {/* 북마크 수 */}
                        <span className={`text-xs font-medium ${diary.isBookmarked ? 'text-[#CF3688]' : 'text-gray-400'}`}>
                            {diary.bookmarkCount || 0}
                        </span>
                    </div>
                </div>
            </div>

            {/* 다이어리 제목 */}
            <div className="mb-1">
                <h4 className="font-bold text-gray-900 text-sm break-words">
                    {diary.title}
                </h4>
                {translatedData && (
                    <h4 className="font-bold text-gray-500 text-sm mt-1 pl-3 border-l-2 border-gray-400 break-words">
                        {translatedData.translatedTitle}
                    </h4>
                )}
            </div>

            {/* 다이어리 내용 */}
            <div className="mb-2">
                <p className="text-gray-600 text-xs line-clamp-6 leading-relaxed">
                    {diary.content}
                </p>
                {translatedData && (
                    <p className="text-gray-500 text-xs line-clamp-6 leading-relaxed mt-1 pl-3 border-l-2 border-gray-400">
                        {translatedData.translatedContent}
                    </p>
                )}
            </div>

            {/* 첨부된 비디오 (가장 먼저 출력) */}
            {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                <div className="mb-2">
                    {diary.diaryVideos.map((diaryVideo, index) => (
                        <VideoPlayer
                            key={index}
                            video={diaryVideo.video}
                            className="w-full rounded-lg"
                        />
                    ))}
                </div>
            )}

            {/* 첨부된 이미지 */}
            {diary.diaryImages && diary.diaryImages.length > 0 && (
                <div className="mb-2">
                    <div className="flex gap-1 overflow-x-auto pb-1">
                        {diary.diaryImages.map((diaryImage, index) => (
                            <div
                                key={index}
                                className="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(diary.diaryImages, index);
                                }}
                            >
                                <img
                                    src={getImageUrl(diaryImage.image, 120, 120)}
                                    alt={`Diary image ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = "/imgs/dummy/basic-img.png";
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 다이어리 메타 정보 영역 */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                    <div
                        className={`flex items-center space-x-1 transition-all duration-200 ${isLiking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer hover:opacity-80'
                            }`}
                        onClick={(e) => !isLiking && onLikeToggle(diary, e)}
                    >
                        {isLiking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 일반 좋아요 아이콘
                            diary.isLiked ? (
                                <img src="/imgs/icons/icon-heart-active.svg" alt="like" className="w-4 h-4" />
                            ) : (
                                <img src="/imgs/icons/icon-heart.svg" alt="like" className="w-4 h-4" />
                            )
                        )}
                        <span className={`text-xs font-medium ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-400'}`}>
                            {diary.likeCount || 0}
                        </span>
                    </div>
                    <div
                        className={`flex items-center space-x-1 cursor-pointer transition-colors text-gray-400`}
                        onClick={(e) => onCommentClick(diary.id, e)}>
                        <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-4 h-4" />
                        <span className="text-xs font-medium">
                            {diary.commentCount || 0}
                        </span>
                    </div>
                </div>
                <div className="flex items-center space-x-1">
                    <span onClick={(e) => onCommentClick(diary.id, e)} className="text-xs font-medium text-[#CF3688]">
                        자세히 보기
                    </span>
                </div>
            </div>

            {/* 활동영역 - 좋아요한 유저들 */}
            {diary.likes && diary.likes.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                    <div
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                        onClick={() => onShowLikePopup(diary.id)}
                    >
                        {/* 좋아요한 유저들의 프로필 사진 */}
                        <div className="flex -space-x-2">
                            {diary.likes.slice(0, 3).map((like, index) => (
                                <div
                                    key={like.userId}
                                    className="w-6 h-6 rounded-full border-2 border-white overflow-hidden shadow-sm"
                                    style={{ zIndex: 5 - index }}
                                >
                                    <img
                                        src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                        alt={like?.user?.userStudent?.nickname || like?.user?.username || "User"}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = "/imgs/dummy/basic-img.png";
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* 좋아요 텍스트 - 우선순위별 조건 */}
                        {diary.castingManagerLikeCount !== undefined && diary.castingManagerLikeCount > 0 ? (
                            /* 1순위: 캐스팅 매니저 좋아요가 있는 경우 */
                            <div 
                                className="flex items-center space-x-1 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShowLikePopup(diary.id);
                                }}
                            >
                                <span className="text-gray-500">
                                    {diary.castingManagerLikeCount === 1 
                                        ? getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtCastingManagerLikeSingle")
                                        : getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtCastingManagerLikeMultiple")
                                    }
                                </span>
                            </div>
                        ) : (
                            /* 2순위: 캐스팅 매니저 좋아요가 없고, 일반 좋아요가 3개 초과인 경우 */
                            diary.likeCount > 3 && (
                                <div 
                                    className="flex items-center space-x-1 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onShowLikePopup(diary.id);
                                    }}
                                >
                                    <span className="text-gray-500">
                                        {getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtOthersLike")}
                                    </span>
                                </div>
                            )
                        )}

                        {/* 화살표 아이콘 */}
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
            )}

            {/* 추가 포스팅 알림 버튼 */}
            {diary.extraDiaryList && diary.extraDiaryList.length > 0 && !showExtraDiaries && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                        className="w-full text-center py-2 px-3 text-[#CF3688] text-sm font-medium hover:text-[#CF3688]/80 transition-colors duration-200"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowExtraDiaries(true);
                        }}
                    >
                        +{diary.extraDiaryList.length} 개의 다이어리가 더 있어요~
                    </button>
                </div>
            )}

            {/* 추가 다이어리 목록 */}
            {showExtraDiaries && diary.extraDiaryList && diary.extraDiaryList.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                    {diary.extraDiaryList.map((extraDiary) => (
                        <div
                            key={extraDiary.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleExtraDiaryClick(extraDiary.id);
                            }}
                        >
                            <div className="flex-1 min-w-0">
                                <span className="text-sm text-[#CF3688] font-medium block truncate">
                                    {extraDiary.title}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {formattedTimeForLocale(extraDiary.createdAt, 'ko-KR')}
                                </span>
                            </div>
                            <div className="flex items-center space-x-3 ml-2">
                                <div className="flex items-center space-x-1">
                                    <img src="/imgs/icons/icon-heart.svg" alt="like" className="w-3 h-3" />
                                    <span className="text-xs text-gray-500 font-medium">
                                        {extraDiary.likeCount || 0}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-3 h-3" />
                                    <span className="text-xs text-gray-500 font-medium">
                                        {extraDiary.commentCount || 0}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <img src="/imgs/icons/icon-bookmark.svg" alt="bookmark" className="w-3 h-3" />
                                    <span className="text-xs text-gray-500 font-medium">
                                        {extraDiary.bookmarkCount || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// 날짜 헤더 스켈레톤 컴포넌트
const DateHeaderSkeleton = () => (
    <div className="flex items-center py-2 animate-pulse">
        <div className="flex-1 border-t border-gray-100"></div>
        <div className="px-3 py-1">
            <div className="h-3 bg-gray-200 rounded w-20"></div>
        </div>
        <div className="flex-1 border-t border-gray-100"></div>
    </div>
);

// 다이어리 스켈레톤 컴포넌트
const DiaryItemSkeleton = () => (
    <div className="border border-gray-200 rounded-lg p-3 animate-pulse">
        {/* 다이어리 헤더 스켈레톤 */}
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                <div className="space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                    <div className="h-2 bg-gray-200 rounded w-24"></div>
                </div>
            </div>
            <div className="h-5 bg-gray-200 rounded-full w-16"></div>
        </div>

        {/* 제목과 내용 스켈레톤 */}
        <div className="mb-2 space-y-1">
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-4/5"></div>
        </div>

        {/* 이미지 스켈레톤 */}
        <div className="mb-2">
            <div className="flex gap-1">
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
            </div>
        </div>

        {/* 메타 정보 스켈레톤 */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-6"></div>
                </div>
                <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-6"></div>
                </div>
            </div>
            <div className="h-3 bg-gray-200 rounded w-16"></div>
        </div>

        {/* 추가 포스팅 버튼 스켈레톤 */}
        <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="w-full h-8 bg-gray-200 rounded-lg"></div>
        </div>

        {/* 추가 다이어리 목록 스켈레톤 */}
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-1"></div>
                    <div className="h-2 bg-gray-200 rounded w-12"></div>
                </div>
                <div className="flex items-center space-x-3 ml-2">
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-200 rounded"></div>
                        <div className="h-2 bg-gray-200 rounded w-4"></div>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-200 rounded"></div>
                        <div className="h-2 bg-gray-200 rounded w-4"></div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-1"></div>
                    <div className="h-2 bg-gray-200 rounded w-10"></div>
                </div>
                <div className="flex items-center space-x-3 ml-2">
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-200 rounded"></div>
                        <div className="h-2 bg-gray-200 rounded w-3"></div>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-200 rounded"></div>
                        <div className="h-2 bg-gray-200 rounded w-3"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// 타임라인 스켈레톤 컴포넌트
const TimelineSkeleton = () => (
    <div className="space-y-5">
        {/* 첫 번째 날짜 그룹 */}
        <div>
            <DateHeaderSkeleton />
            <div className="space-y-3">
                <DiaryItemSkeleton />
                <DiaryItemSkeleton />
            </div>
        </div>
        
        {/* 두 번째 날짜 그룹 */}
        <div>
            <DateHeaderSkeleton />
            <div className="space-y-3">
                <DiaryItemSkeleton />
            </div>
        </div>
    </div>
);

interface DiaryTimelineProps {
    title?: string;
    maxItems?: number;
}

const DiaryTimeline: React.FC<DiaryTimelineProps> = ({
    title = "지망생 다이어리",
    maxItems = 5
}) => {
    const { showPopup } = usePopup();
    const [diaryData, setDiaryData] = useState<IStudentDiariesResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [likingDiaryIds, setLikingDiaryIds] = useState<Set<number>>(new Set()); // 좋아요 처리 중인 다이어리 ID들
    const [bookmarkingDiaryIds, setBookmarkingDiaryIds] = useState<Set<number>>(new Set()); // 북마크 처리 중인 다이어리 ID들
    const [translatingDiaryIds, setTranslatingDiaryIds] = useState<Set<number>>(new Set()); // 번역 처리 중인 다이어리 ID들
    const [translatedDiaries, setTranslatedDiaries] = useState<Map<number, ITranslateResponse>>(new Map()); // 번역된 다이어리 데이터

    // 다이어리 목록 조회
    const fetchDiaryList = useCallback(async () => {
        try {
            setIsLoading(true);
            console.log("🔄 첫 번째 페이지 다이어리 목록 조회 시작");
            const result = await getStudentDiaries(1);
            console.log("📊 첫 번째 페이지 API 응답:", result);

            if (result?.diaryList) {
                setDiaryData(result);
                setHasMore(result.pagination?.hasNextPage || false);
                setCurrentPage(1);
                console.log("✅ 첫 번째 페이지 데이터 설정 완료", {
                    itemCount: result.diaryList.length,
                    hasNextPage: result.pagination?.hasNextPage
                });
            } else {
                console.log("⚠️ 다이어리 목록이 비어있음");
                setDiaryData({ diaryList: [], pagination: { hasNextPage: false } as any });
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 다이어리 목록 조회 실패:", error);
            setDiaryData({ diaryList: [], pagination: { hasNextPage: false } as any });
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 추가 데이터 로드 (무한 스크롤용)
    const loadMoreData = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore, currentPage });
            return;
        }

        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            console.log("📄 다음 페이지 로드 시작:", nextPage);

            const result = await getStudentDiaries(nextPage);
            console.log("📊 다음 페이지 API 응답:", { page: nextPage, result });

            if (result?.diaryList && result.diaryList.length > 0) {
                setDiaryData(prevData => {
                    if (!prevData) {
                        console.log("⚠️ 이전 데이터가 없음, 새 데이터로 설정");
                        return result;
                    }

                    const newData = {
                        ...prevData,
                        diaryList: [...prevData.diaryList, ...result.diaryList],
                        pagination: result.pagination
                    };
                    console.log("📊 업데이트된 다이어리 데이터:", {
                        previousCount: prevData.diaryList.length,
                        newCount: result.diaryList.length,
                        totalCount: newData.diaryList.length,
                        hasNextPage: result.pagination?.hasNextPage
                    });
                    return newData;
                });
                setCurrentPage(nextPage);
                setHasMore(result.pagination?.hasNextPage || false);
                console.log("✅ 페이지 상태 업데이트:", {
                    newPage: nextPage,
                    hasMore: result.pagination?.hasNextPage || false
                });
            } else {
                console.log("📝 더 이상 로드할 데이터가 없음 (빈 배열 또는 null)");
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
            console.log("🔄 로딩 상태 해제");
        }
    }, [currentPage, hasMore, isLoadingMore]);

    // 좋아요 토글 처리
    const handleLikeToggle = async (diary: IDiary, event: React.MouseEvent) => {
        event.stopPropagation(); // 다이어리 클릭 이벤트 방지

        // 이미 좋아요 처리 중인 다이어리인지 확인
        if (likingDiaryIds.has(diary.id)) {
            console.log("🚫 이미 좋아요 처리 중인 다이어리:", diary.id);
            return;
        }

        try {
            // 좋아요 처리 중인 다이어리 ID 추가
            setLikingDiaryIds(prev => new Set(prev).add(diary.id));
            console.log("❤️ 좋아요 토글 시작:", diary.id);

            const result = await toggleDiaryLike(diary.id.toString());
            console.log("좋아요 토글 결과:", result);

            // 다이어리 목록에서 해당 다이어리의 좋아요 상태와 수 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;

                return {
                    ...prevData,
                    diaryList: prevData.diaryList.map(item =>
                        item.id === diary.id
                            ? {
                                ...item,
                                isLiked: result.isLiked,
                                likeCount: result.likeCount
                            }
                            : item
                    )
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 좋아요 처리 완료 후 ID 제거
            setLikingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diary.id);
                return newSet;
            });
            console.log("✅ 좋아요 토글 완료:", diary.id);
        }
    };

    // 북마크 토글 처리
    const handleBookmarkToggle = async (diary: IDiary, event: React.MouseEvent) => {
        event.stopPropagation(); // 다이어리 클릭 이벤트 방지

        // 이미 북마크 처리 중인 다이어리인지 확인
        if (bookmarkingDiaryIds.has(diary.id)) {
            console.log("🚫 이미 북마크 처리 중인 다이어리:", diary.id);
            return;
        }

        try {
            // 북마크 처리 중인 다이어리 ID 추가
            setBookmarkingDiaryIds(prev => new Set(prev).add(diary.id));
            console.log("🔖 북마크 토글 시작:", diary.id);

            const result = await toggleDiaryBookmark(diary.id.toString());
            console.log("북마크 토글 결과:", result);

            // 다이어리 목록에서 해당 다이어리의 북마크 상태와 수 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;

                return {
                    ...prevData,
                    diaryList: prevData.diaryList.map(item =>
                        item.id === diary.id
                            ? {
                                ...item,
                                isBookmarked: result.isBookmarked,
                                bookmarkCount: result.bookmarkCount
                            }
                            : item
                    )
                };
            });

        } catch (error) {
            console.error("북마크 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 북마크 처리 완료 후 ID 제거
            setBookmarkingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diary.id);
                return newSet;
            });
            console.log("✅ 북마크 토글 완료:", diary.id);
        }
    };

    // 번역 처리
    const handleTranslate = async (diary: IDiary, event: React.MouseEvent) => {
        event.stopPropagation(); // 다이어리 클릭 이벤트 방지

        // 이미 번역 처리 중인 다이어리인지 확인
        if (translatingDiaryIds.has(diary.id)) {
            console.log("🚫 이미 번역 처리 중인 다이어리:", diary.id);
            return;
        }

        // 이미 번역된 다이어리인지 확인
        if (translatedDiaries.has(diary.id)) {
            console.log("✅ 이미 번역된 다이어리:", diary.id);
            return;
        }

        try {
            // 번역 처리 중인 다이어리 ID 추가
            setTranslatingDiaryIds(prev => new Set(prev).add(diary.id));
            console.log("🌐 번역 시작:", diary.id);

            const result = await translateDiary(diary.id.toString());
            console.log("번역 결과:", result);

            // 번역된 다이어리 데이터 저장
            setTranslatedDiaries(prev => new Map(prev).set(diary.id, result));

        } catch (error) {
            console.error("번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 번역 처리 완료 후 ID 제거
            setTranslatingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diary.id);
                return newSet;
            });
            console.log("✅ 번역 완료:", diary.id);
        }
    };

    // 무한 스크롤 이벤트 핸들러
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            // 이미 스크롤 처리 중이거나 로딩 중이거나 다음 페이지가 없으면 무시
            if (isScrolling || isLoadingMore || !hasMore) {
                console.log("🚫 스크롤 이벤트 무시:", { isScrolling, isLoadingMore, hasMore });
                return;
            }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = scrollTop + windowHeight;
            const triggerPosition = documentHeight - 200; // 200px 여유

            // console.log("📏 스크롤 위치 체크:", {
            //     scrollTop,
            //     windowHeight,
            //     documentHeight,
            //     scrollPosition,
            //     triggerPosition,
            //     shouldTrigger: scrollPosition >= triggerPosition
            // });

            // 스크롤이 하단 근처에 도달했는지 확인
            if (scrollPosition >= triggerPosition) {
                console.log("🔄 스크롤 임계값 도달, 다음 페이지 로드 시작");
                isScrolling = true; // 스크롤 처리 시작

                loadMoreData().finally(() => {
                    // API 호출 완료 후 플래그 리셋 (1초 후)
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        console.log("🔄 스크롤 플래그 리셋");
                    }, 1000);
                });
            }
        };

        // 스크롤 이벤트 리스너 등록
        window.addEventListener('scroll', handleScroll, { passive: true });
        console.log("👂 스크롤 이벤트 리스너 등록 완료");

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
            console.log("🗑️ 스크롤 이벤트 리스너 제거");
        };
    }, [isLoadingMore, hasMore, loadMoreData]);

    // 이미지 클릭 시 팝업 표시
    const handleImageClick = (images: any[], initialIndex: number = 0) => {
        showPopup(
            <ImageSlidePopup
                imageInfoList={images.map(img => img.image)}
                initialSlideIdx={initialIndex}
                onClose={() => { }}
            />
        );
    };

    // 다이어리 클릭 시 상세 화면으로 이동 (필요시 구현)
    const handleDiaryClick = (diaryId: number) => {
        // TODO: 다이어리 상세 팝업 또는 페이지 이동 구현
        console.log("다이어리 클릭:", diaryId);
    };

    // 코멘트 버튼 클릭 시 다이어리 상세 팝업 표시
    const handleCommentClick = (diaryId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // 다이어리 클릭 이벤트 전파 방지
        showPopup(
            <StudentDiaryDetailPopup
                diaryId={diaryId.toString()}
                onClose={() => {
                    // 팝업이 닫힐 때 다이어리 목록 새로고침
                    fetchDiaryList();
                }}
            />
        );
    };

    useEffect(() => {
        fetchDiaryList();
    }, [fetchDiaryList]);

    if (isLoading) {
        return (
            <div className="shadow-sm p-2">
                <h2 className="text-base font-bold text-gray-900 mb-3">
                    {title}
                </h2>
                <TimelineSkeleton />
            </div>
        );
    }

    if (!diaryData || diaryData.diaryList.length === 0) {
        return (
            <div className="shadow-sm p-2">
                <h2 className="text-base font-bold text-gray-900 mb-3">
                    {title}
                </h2>
                <div className="text-center py-4">
                    <p className="text-gray-400 text-sm">
                        아직 작성된 다이어리가 없습니다.
                    </p>
                </div>
            </div>
        );
    }

    const handleStudentInfoPopup = (userStudentId: number) => {
        showPopup(
            <StudentProfilePopup
                userStudentId={userStudentId}
                onClose={() => {}}
            />
        );
    }

    // 좋아요 목록 팝업 표시 함수
    const handleShowLikePopup = (diaryId: number) => {
        const popupContent = (
            <LikePopupForDiary
                diaryId={diaryId.toString()}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 날짜별로 다이어리 그룹화
    const groupDiariesByDate = (diaries: IDiary[]) => {
        const groups: { [key: string]: IDiary[] } = {};
        
        diaries.forEach(diary => {
            const date = new Date(diary.createdAt);
            const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD 형식
            
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(diary);
        });
        
        // 날짜 순으로 정렬 (최신 날짜가 먼저)
        return Object.keys(groups)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(dateKey => ({
                date: dateKey,
                diaries: groups[dateKey]
            }));
    };

    console.log("🎨 DiaryTimeline 렌더링:", {
        diaryCount: diaryData?.diaryList?.length || 0,
        hasMore,
        isLoadingMore,
        currentPage
    });

    const groupedDiaries = groupDiariesByDate(diaryData.diaryList);

    return (
        <>
            <h2 className="text-base font-bold text-gray-900 mb-2">
                {title}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
                지망생들이 작성한 성장 다이어리입니다. 그들의 실력과 잠재력을 확인해보세요.
            </p>

            <div className="space-y-5 -mx-2">
                {groupedDiaries.map((group, groupIndex) => (
                    <div key={group.date}>
                        {/* 날짜 헤더 */}
                        <DateHeader date={group.date} />
                        
                        {/* 해당 날짜의 다이어리들 */}
                        <div className="space-y-3">
                            {group.diaries.map((diary) => {
                                const isLiking = likingDiaryIds.has(diary.id);
                                const isBookmarking = bookmarkingDiaryIds.has(diary.id);
                                const isTranslating = translatingDiaryIds.has(diary.id);
                                const translatedData = translatedDiaries.get(diary.id);

                                return (
                                    <DiaryItem
                                        key={diary.id}
                                        diary={diary}
                                        isLiking={isLiking}
                                        isBookmarking={isBookmarking}
                                        isTranslating={isTranslating}
                                        translatedData={translatedData}
                                        onLikeToggle={handleLikeToggle}
                                        onBookmarkToggle={handleBookmarkToggle}
                                        onTranslate={handleTranslate}
                                        onCommentClick={handleCommentClick}
                                        onStudentInfoPopup={handleStudentInfoPopup}
                                        onImageClick={handleImageClick}
                                        onShowLikePopup={handleShowLikePopup}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* 추가 로딩 상태 */}
                {isLoadingMore && (
                    <div className="flex justify-center items-center py-8">
                        <div className="flex items-center space-x-2">
                            <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {/* <span className="text-sm text-gray-500">더 많은 포스트를 불러오는 중...</span> */}
                        </div>
                    </div>
                )}

                {/* 더 이상 로드할 데이터가 없을 때 */}
                {!isLoadingMore && !hasMore && diaryData.diaryList.length > 0 && (
                    <div className="flex justify-center items-center py-8">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            {/* <p className="text-gray-500 text-sm">모든 포스트를 불러왔습니다</p> */}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DiaryTimeline;
